import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, AddPhotoAlternate as AddPhotoIcon } from '@mui/icons-material';
import axios from 'axios';
import { updatePassword } from 'aws-amplify/auth';
import { useAuth } from '../auth/AuthContext';
import { formatPasswordSignInError } from '../utils/authErrors';
import RoleChip from './RoleChip';
import { apiClient } from '../api/client';
import { DEFAULT_AVATAR_URL } from '../constants/defaultAvatar';

type AvatarPresignResponse = {
  upload_url: string;
  method: string;
  headers: Record<string, string>;
  avatar_url: string;
};

const ACCEPT_IMAGE = 'image/jpeg,image/png,image/webp,image/gif';

const Profile: React.FC = () => {
  const { staffProfile, staffProfileLoading, staffProfileError, refreshStaffProfile } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [editing, setEditing] = useState(false);
  const [displayNameDraft, setDisplayNameDraft] = useState('');
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);
  const [clearAvatar, setClearAvatar] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);
  /** メール＋パスワード利用者向けパスワード変更フォームの表示 */
  const [pwFormOpen, setPwFormOpen] = useState(false);

  useEffect(() => {
    return () => {
      if (previewObjectUrl) {
        URL.revokeObjectURL(previewObjectUrl);
      }
    };
  }, [previewObjectUrl]);

  const startEdit = useCallback(() => {
    if (!staffProfile) return;
    setDisplayNameDraft(staffProfile.display_name);
    setPickedFile(null);
    setClearAvatar(false);
    setFormError(null);
    setPreviewObjectUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setEditing(true);
  }, [staffProfile]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setPickedFile(null);
    setClearAvatar(false);
    setFormError(null);
    setPreviewObjectUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const closePasswordForm = useCallback(() => {
    setPwFormOpen(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPwError(null);
  }, []);

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!ACCEPT_IMAGE.split(',').includes(f.type)) {
      setFormError('JPEG / PNG / WebP / GIF のみ選択できます。');
      return;
    }
    setFormError(null);
    setClearAvatar(false);
    setPickedFile(f);
    setPreviewObjectUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
  };

  const savePassword = async () => {
    setPwError(null);
    setPwSuccess(false);
    if (newPassword.length < 8) {
      setPwError('新しいパスワードは8文字以上にしてください。');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('新しいパスワードが一致しません。');
      return;
    }
    if (!oldPassword) {
      setPwError('現在のパスワードを入力してください。');
      return;
    }
    setPwSaving(true);
    try {
      await updatePassword({ oldPassword, newPassword });
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPwFormOpen(false);
      setPwSuccess(true);
    } catch (e: unknown) {
      setPwError(formatPasswordSignInError(e));
    } finally {
      setPwSaving(false);
    }
  };

  const save = async () => {
    if (!staffProfile) return;
    setFormError(null);

    const nameChanged = displayNameDraft.trim() !== staffProfile.display_name;
    const avatarChanged = clearAvatar || pickedFile !== null;

    if (!nameChanged && !avatarChanged) {
      setEditing(false);
      return;
    }

    if (nameChanged && !displayNameDraft.trim()) {
      setFormError('名前は1文字以上入力してください。');
      return;
    }

    setSaving(true);
    try {
      const patch: { display_name?: string; avatar_url?: string | null } = {};
      if (nameChanged) {
        patch.display_name = displayNameDraft.trim().slice(0, 100);
      }
      if (clearAvatar) {
        patch.avatar_url = null;
      } else if (pickedFile) {
        let contentType = pickedFile.type;
        if (!contentType) {
          contentType = 'image/jpeg';
        }
        try {
          const presignRes = await apiClient.post<AvatarPresignResponse>('/api/me/avatar/presign', {
            content_type: contentType,
          });
          const { upload_url, headers } = presignRes.data;
          const putRes = await fetch(upload_url, {
            method: 'PUT',
            body: pickedFile,
            headers,
          });
          if (!putRes.ok) {
            throw new Error('upload_failed');
          }
          patch.avatar_url = presignRes.data.avatar_url;
        } catch (e: unknown) {
          const data =
            axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
              ? (e.response.data as { error?: string; detail?: string })
              : null;
          const status = axios.isAxiosError(e) ? e.response?.status : undefined;
          if (status === 503) {
            setFormError(
              data?.error ||
                '画像アップロードは現在利用できません（サーバー側の S3 設定を確認してください）。名前のみ保存する場合は画像選択を解除してください。'
            );
            setSaving(false);
            return;
          }
          if (data?.error) {
            setFormError([data.error, data.detail].filter(Boolean).join(' — '));
            setSaving(false);
            return;
          }
          setFormError('画像のアップロードに失敗しました。');
          setSaving(false);
          return;
        }
      }

      await apiClient.patch('/api/me', patch);
      await refreshStaffProfile();
      cancelEdit();
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
          ? (e.response.data as { error?: string }).error
          : null;
      setFormError(msg || '保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  if (staffProfileLoading) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Typography>読み込み中…</Typography>
      </Box>
    );
  }

  if (staffProfileError === 'other') {
    return (
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refreshStaffProfile()}>
              再読み込み
            </Button>
          }
        >
          プロフィールの取得に失敗しました。
        </Alert>
      </Box>
    );
  }

  if (!staffProfile) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Alert severity="warning">
          職員として登録されていないため、プロフィールを表示できません。
        </Alert>
      </Box>
    );
  }

  const avatarSrc = clearAvatar
    ? DEFAULT_AVATAR_URL
    : previewObjectUrl ?? staffProfile.avatar_url ?? DEFAULT_AVATAR_URL;

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">プロフィール</Typography>
      </Box>

      <Paper
        sx={{
          p: 3,
          maxWidth: 480,
          width: '100%',
          mx: 'auto',
          position: 'relative',
        }}
      >
        {!editing && (
          <IconButton
            aria-label="プロフィールを編集"
            onClick={startEdit}
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <EditIcon />
          </IconButton>
        )}

        {formError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError(null)}>
            {formError}
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            mb: 2,
          }}
        >
          <Avatar
            src={avatarSrc}
            alt=""
            sx={{ width: 96, height: 96, fontSize: 40 }}
          >
            {(editing ? displayNameDraft : staffProfile.display_name).charAt(0) || '?'}
          </Avatar>

          {editing ? (
            <Stack spacing={1.5} sx={{ width: '100%' }}>
              <TextField
                label="名前"
                value={displayNameDraft}
                onChange={(e) => setDisplayNameDraft(e.target.value)}
                fullWidth
                size="small"
                inputProps={{ maxLength: 100 }}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_IMAGE}
                hidden
                onChange={onPickFile}
              />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ alignItems: 'center' }}>
                <Button variant="text" size="small" startIcon={<AddPhotoIcon />} onClick={() => fileInputRef.current?.click()}>
                  画像を選ぶ
                </Button>
                {(staffProfile.avatar_url || pickedFile) && !pickedFile && (
                  <Button
                    variant="text"
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      setClearAvatar(true);
                      setPickedFile(null);
                      setPreviewObjectUrl((prev) => {
                        if (prev) URL.revokeObjectURL(prev);
                        return null;
                      });
                    }}
                  >
                    アイコンを削除
                  </Button>
                )}
                {pickedFile && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setPickedFile(null);
                      setPreviewObjectUrl((prev) => {
                        if (prev) URL.revokeObjectURL(prev);
                        return null;
                      });
                    }}
                  >
                    画像選択を解除
                  </Button>
                )}
              </Stack>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="small" onClick={cancelEdit} disabled={saving}>
                  キャンセル
                </Button>
                <Button size="small" variant="contained" onClick={() => void save()} disabled={saving}>
                  {saving ? '保存中…' : '保存'}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Typography variant="h6" textAlign="center">
              {staffProfile.display_name}
            </Typography>
          )}
        </Box>

        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              メールアドレス
            </Typography>
            <Typography variant="body2">{staffProfile.email}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              役職
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <RoleChip role={staffProfile.role} />
            </Box>
          </Box>
        </Stack>
      </Paper>

      {staffProfile.auth_method === 'email_password' && (
        <Paper
          sx={{
            p: 3,
            maxWidth: 480,
            width: '100%',
            mx: 'auto',
            mt: 3,
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            パスワード
          </Typography>
          {pwSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPwSuccess(false)}>
              パスワードを更新しました。
            </Alert>
          )}
          {!pwFormOpen ? (
            <Button
              variant="outlined"
              size="medium"
              onClick={() => {
                setPwSuccess(false);
                setPwFormOpen(true);
              }}
            >
              パスワードを変更
            </Button>
          ) : (
            <>
              {pwError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPwError(null)}>
                  {pwError}
                </Alert>
              )}
              <Stack spacing={1.5}>
                <TextField
                  label="現在のパスワード"
                  type="password"
                  fullWidth
                  size="small"
                  autoComplete="current-password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                />
                <TextField
                  label="新しいパスワード"
                  type="password"
                  fullWidth
                  size="small"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <TextField
                  label="新しいパスワード（確認）"
                  type="password"
                  fullWidth
                  size="small"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <Typography variant="caption" color="text.secondary">
                  8文字以上・大文字・小文字・数字・記号を含めてください。
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button size="small" onClick={closePasswordForm} disabled={pwSaving}>
                    キャンセル
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={pwSaving}
                    onClick={() => void savePassword()}
                  >
                    {pwSaving ? '更新中…' : 'パスワードを更新'}
                  </Button>
                </Stack>
              </Stack>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default Profile;
